import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

// ============================================================
// SkylieCreates
// Header menu interaction settings
// ============================================================

// Desktop hover intent.
// A small delay prevents accidental switching when the cursor
// moves diagonally between neighboring top-level menu items.
const ACTIVATE_DELAY = 150;

// Small delay when leaving the menu so the cursor can naturally
// travel from the parent item into the mega menu.
const DEACTIVATE_DELAY = 250;


/**
 * A custom element that manages the header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 * @property {HTMLElement | null} touchActivatedItem
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu]
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /**
   * ============================================================
   * CONNECTED
   * ============================================================
   */
  connectedCallback() {
    super.connectedCallback();

    // ----------------------------------------------------------
    // Desktop:
    // Close dropdown when pointer completely leaves header-menu.
    // ----------------------------------------------------------
    this.addEventListener(
      'pointerleave',
      this.#handlePointerLeave,
      {
        signal: this.#abortController.signal,
      }
    );

    // ----------------------------------------------------------
    // Overflow menu:
    // Keep the dropdown open while pointer is inside it.
    // ----------------------------------------------------------
    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#handlePointerLeave,
      {
        signal: this.#abortController.signal,
      }
    );

    // ----------------------------------------------------------
    // Touch / iPad:
    //
    // IMPORTANT:
    // We use pointerdown instead of pointerup.
    //
    // Safari/iPad can start link navigation through the generated
    // click after pointerup. Preventing pointerup is therefore
    // not reliable enough.
    //
    // pointerdown happens early enough to stop the first tap
    // from navigating.
    // ----------------------------------------------------------
    this.addEventListener(
      'pointerdown',
      this.#handleMenuPointerDown,
      {
        signal: this.#abortController.signal,
      }
    );

    // ----------------------------------------------------------
    // Touch / iPad fallback:
    //
    // Some browsers may still generate a click after pointerdown.
    // This handler guarantees that the first tap cannot navigate.
    // ----------------------------------------------------------
    this.addEventListener(
      'click',
      this.#handleMenuClick,
      {
        signal: this.#abortController.signal,
      }
    );

    // ----------------------------------------------------------
    // Any device:
    // Close dropdown when clicking/tapping outside the menu.
    // ----------------------------------------------------------
    document.addEventListener(
      'pointerdown',
      this.#handleDocumentPointerDown,
      {
        signal: this.#abortController.signal,
        capture: true,
      }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  /**
   * ============================================================
   * DISCONNECTED
   * ============================================================
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * ============================================================
   * STATE
   * ============================================================
   */
  #state = {
    // Currently opened parent menu item.
    activeItem: null,

    // Parent item that was activated by the first touch tap.
    //
    // First tap:
    //   open dropdown
    //
    // Second tap:
    //   follow link
    //
    touchActivatedItem: null,
  };

  /**
   * ============================================================
   * ANIMATION DELAY
   * ============================================================
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;

    return value ? parseInt(value, 10) : 0;
  }

  /**
   * ============================================================
   * OVERFLOW MENU
   * ============================================================
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      )
    );
  }

  /**
   * ============================================================
   * OVERFLOW HOVERED
   * ============================================================
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * ============================================================
   * TOUCH DEVICE DETECTION
   * ============================================================
   *
   * Normally iPad reports:
   *
   *   pointerType === "touch"
   *
   * The media-query fallback helps with iPad simulators and
   * browsers that emulate touch behavior.
   */
  #isTouchInteraction(event) {
    if (!(event instanceof PointerEvent)) {
      return false;
    }

    if (event.pointerType === 'touch') {
      return true;
    }

    // Fallback for touch/coarse pointer environments.
    if (
      event.pointerType === 'mouse' &&
      window.matchMedia(
        '(hover: none) and (pointer: coarse)'
      ).matches
    ) {
      return true;
    }

    return false;
  }

  /**
   * ============================================================
   * ACTIVATE
   * ============================================================
   *
   * Called from the Liquid inline:
   *
   *   on:pointerenter="/activate"
   *   on:focus="/activate"
   *
   * Desktop pointer activation is delayed.
   * Touch activation is handled separately.
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    // ----------------------------------------------------------
    // Pointer interaction
    // ----------------------------------------------------------
    if (event instanceof PointerEvent) {
      // Touch should NOT activate through hover events.
      //
      // Touch is handled by pointerdown.
      if (this.#isTouchInteraction(event)) {
        return;
      }

      // Desktop mouse:
      // use hover-intent delay.
      this.#debouncedActivateHandler(event);

      return;
    }

    // ----------------------------------------------------------
    // Keyboard / focus
    // ----------------------------------------------------------
    this.#activateHandler(event);
  };

  /**
   * ============================================================
   * FIRST TOUCH / iPAD POINTERDOWN
   * ============================================================
   *
   * This is the important part.
   *
   * First tap:
   *   prevent navigation
   *   open dropdown
   *
   * Second tap:
   *   allow normal link navigation
   *
   * Different parent:
   *   switch dropdown
   *   prevent navigation
   */
  #handleMenuPointerDown = (event) => {
    if (!(event instanceof PointerEvent)) {
      return;
    }

    // Ignore desktop mouse.
    if (!this.#isTouchInteraction(event)) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    // Only top-level menu links.
    const link = event.target.closest(
      'a[ref="menuitem"]'
    );

    if (!(link instanceof HTMLElement)) {
      return;
    }

    const item = findMenuItem(link);

    if (!item) {
      return;
    }

    // Cancel desktop hover timers.
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    // ==========================================================
    // SECOND TAP ON SAME PARENT
    // ==========================================================
    if (this.#state.touchActivatedItem === item) {
      /*
       * IMPORTANT:
       *
       * Do NOT call preventDefault().
       *
       * The browser is now allowed to follow:
       *
       *   link.href
       *
       * This is the second tap -> navigation.
       */

      this.#state.touchActivatedItem = null;

      return;
    }

    // ==========================================================
    // FIRST TAP
    // ==========================================================

    /*
     * CRITICAL:
     *
     * preventDefault() happens on pointerdown.
     *
     * This prevents Safari/iPad from continuing into the
     * normal anchor navigation sequence.
     */
    event.preventDefault();

    // Remember this parent.
    this.#state.touchActivatedItem = item;

    // Open dropdown immediately.
    this.#activateHandler(event);
  };

  /**
   * ============================================================
   * CLICK FALLBACK FOR TOUCH
   * ============================================================
   *
   * Normally pointerdown.preventDefault() is enough.
   *
   * This provides an additional safety net for browsers /
   * simulators that still generate a click.
   */
  #handleMenuClick = (event) => {
    if (!(event instanceof MouseEvent)) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    const link = event.target.closest(
      'a[ref="menuitem"]'
    );

    if (!(link instanceof HTMLElement)) {
      return;
    }

    /*
     * We only use this fallback when a touch interaction has
     * already activated a parent.
     */
    if (!this.#state.touchActivatedItem) {
      return;
    }

    const item = findMenuItem(link);

    if (!item) {
      return;
    }

    /*
     * If this is still the first-tap item, the click must NOT
     * navigate.
     *
     * This is especially useful for iPad simulators.
     */
    if (this.#state.touchActivatedItem === item) {
      event.preventDefault();
      event.stopPropagation();

      return;
    }
  };

  /**
   * ============================================================
   * CLICK / TAP OUTSIDE
   * ============================================================
   */
  #handleDocumentPointerDown = (event) => {
    if (!(event instanceof PointerEvent)) {
      return;
    }

    const path = event.composedPath();

    // Click happened inside this header menu.
    if (path.includes(this)) {
      return;
    }

    // Cancel pending desktop activation.
    this.#debouncedActivateHandler.cancel();

    // Cancel pending desktop deactivation.
    this.#debouncedDeactivate.cancel();

    // Reset touch state.
    this.#state.touchActivatedItem = null;

    // Close immediately.
    this.#deactivate();
  };

  /**
   * ============================================================
   * POINTER LEAVE
   * ============================================================
   *
   * Handles:
   *
   * 1. Cursor leaving downward
   * 2. Cursor leaving upward
   * 3. Cursor moving away from the header completely
   *
   * The delayed close still gives enough time to enter the
   * mega menu naturally.
   */
  #handlePointerLeave = () => {
    this.#debouncedActivateHandler.cancel();

    this.#debouncedDeactivate();
  };

  /**
   * ============================================================
   * ACTIVATE MENU ITEM
   * ============================================================
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(
      new MegaMenuHoverEvent()
    );

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) {
      return;
    }

    const item = findMenuItem(event.target);

    if (!item) {
      return;
    }

    // Already active.
    if (item === this.#state.activeItem) {
      return;
    }

    // ----------------------------------------------------------
    // Overflow menu
    // ----------------------------------------------------------
    const isDefaultSlot =
      event.target.slot === '';

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    // ----------------------------------------------------------
    // Close previous item
    // ----------------------------------------------------------
    const previouslyActiveItem =
      this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      previouslyActiveItem.removeAttribute(
        'data-animating'
      );
    }

    // ----------------------------------------------------------
    // Set new active item
    // ----------------------------------------------------------
    this.#state.activeItem = item;

    this.ariaExpanded = 'true';

    item.ariaExpanded = 'true';

    // ----------------------------------------------------------
    // Find submenu
    // ----------------------------------------------------------
    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // ----------------------------------------------------------
    // Mark submenu active
    // ----------------------------------------------------------
    if (submenu) {
      submenu.dataset.active = '';
    }

    // ----------------------------------------------------------
    // Calculate submenu height
    // ----------------------------------------------------------
    const submenuHeight =
      submenu ? submenu.offsetHeight : 0;

    this.style.setProperty(
      '--submenu-height',
      `${submenuHeight}px`
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );
  };

  /**
   * ============================================================
   * DEBOUNCED ACTIVATION
   * ============================================================
   */
  #debouncedActivateHandler = debounce(
    this.#activateHandler,
    ACTIVATE_DELAY
  );

  /**
   * ============================================================
   * DEACTIVATE REQUEST
   * ============================================================
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) {
      return;
    }

    const item = findMenuItem(
      event.target
    );

    // Only deactivate if this is still the active item.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * ============================================================
   * DEACTIVATE IMMEDIATELY
   * ============================================================
   */
  #deactivate = (
    item = this.#state.activeItem
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem
    ) {
      return;
    }

    // ----------------------------------------------------------
    // Do not close while pointer is inside overflow menu.
    // ----------------------------------------------------------
    if (this.overflowHovered) {
      return;
    }

    // ----------------------------------------------------------
    // Hide submenu
    // ----------------------------------------------------------
    this.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded = 'false';

    // ----------------------------------------------------------
    // Find submenu
    // ----------------------------------------------------------
    const submenu = findSubmenu(item);

    // ----------------------------------------------------------
    // Reset state
    // ----------------------------------------------------------
    this.#state.activeItem = null;

    this.#state.touchActivatedItem = null;

    this.ariaExpanded = 'false';

    item.ariaExpanded = 'false';

    item.setAttribute(
      'data-animating',
      ''
    );

    // ----------------------------------------------------------
    // Remove animation state after transition
    // ----------------------------------------------------------
    setTimeout(() => {
      item.removeAttribute(
        'data-animating'
      );

      // Remove active state from submenu
      // after animation completes.
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(
      0,
      this.animationDelay - 150
    ));
  };

  /**
   * ============================================================
   * DEBOUNCED DEACTIVATION
   * ============================================================
   */
  #debouncedDeactivate = debounce(
    this.#deactivate,
    DEACTIVATE_DELAY
  );

  /**
   * ============================================================
   * PRELOAD IMAGES
   * ============================================================
   */
  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]'
    );

    images?.forEach((image) => {
      image.removeAttribute('loading');
    });
  };
}

/**
 * ============================================================
 * REGISTER CUSTOM ELEMENT
 * ============================================================
 */
if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}


/**
 * ============================================================
 * FIND MENU ITEM
 * ============================================================
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  // ------------------------------------------------------------
  // "More" menu
  // ------------------------------------------------------------
  if (element.matches('[slot="more"]')) {
    return findMenuItem(
      element.parentElement?.querySelector(
        '[slot="overflow"]'
      )
    );
  }

  // ------------------------------------------------------------
  // Normal menu item
  // ------------------------------------------------------------
  return element.querySelector(
    '[ref="menuitem"]'
  );
}

/**
 * Find the closest submenu.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector(
    '[ref="submenu[]"]'
  );

  return submenu instanceof HTMLElement ? submenu : null;
}
